import React, { useState, useRef, useContext, useMemo } from "react";
import {
  Modal,
  Button,
  Space,
  List,
  Divider,
  Alert,
  Form,
  Input,
  Select,
  message,
  Card,
  Statistic,
  Typography,
  Badge,
  Descriptions,
  Empty,
} from "antd";
import {
  EyeOutlined,
  DollarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { formatCurrency } from "../../helpers/currency";
import { NAIRA, PERCENT } from "../../defaults/utilities";
import { userStore } from "../../store/userStore";
import {
  useSubmitVoucherForApproval,
  useVoucherApproval,
  useVoucherDisapproval,
  useDeleteVoucher,
  useSettleVoucher,
  useReverseSettleVoucher,
  useUploadVoucherRetirement,
  useToggleCheque,
  useToggleClose,
  useVoucherComment,
  useFetchVoucherComments,
  useAcknowledgeVoucher,
  useCheckVoucher,
  useSetChecker,
  useDownloadRetirement,
  useDownloadVoucherDocument,
} from "../../hooks/voucher/voucherQuery";
import { useDownloadMemo } from "../../hooks/memo/memoQuery";
import { useSearchUsers } from "../../hooks/user/userQuery";
import RefreshContext from "../../context/refreshContext";
import DataTable from "../common/table";
import CommentEdit from "./CommentEdit";
import {
  CreatorActions,
  ApprovalActions,
  CashOfficeActions,
  BursarActions,
  CheckerActions,
  AcknowledgmentActions,
  RetirementActions,
  DocumentActions,
  CheckerSelection,
  CheckerInfo,
  ChequeInfo,
} from "./VoucherActions";

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

function VoucherEditRefactored({ voucher }) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRoleForComment, setSelectedRoleForComment] = useState("");
  const [isCommentOpen, setIsCommentOpen] = useState(false);
  const [selectedChecker, setSelectedChecker] = useState(null);
  const [checkerOptions, setCheckerOptions] = useState([]);
  const [checkerSearchLoading, setCheckerSearchLoading] = useState(false);
  const [chequeNumber, setChequeNumber] = useState("");
  const [isSettleModalOpen, setIsSettleModalOpen] = useState(false);

  const currentUser = userStore((state) => state.user);
  const commentAreaRef = useRef(null);
  const { flag, setFlag } = useContext(RefreshContext);

  // Hooks
  const downloadMemo = useDownloadMemo();
  const downloadRet = useDownloadRetirement();
  const downloadVoucher = useDownloadVoucherDocument();
  const searchUsers = useSearchUsers();
  const setChecker = useSetChecker();
  const requestApproval = useSubmitVoucherForApproval();
  const approve = useVoucherApproval();
  const settlePV = useSettleVoucher();
  const toggleCheque = useToggleCheque();
  const toggleClose = useToggleClose();
  const disapprove = useVoucherDisapproval();
  const deleteVoucher = useDeleteVoucher();
  const uploadRetirementDoc = useUploadVoucherRetirement();
  const comment = useVoucherComment();
  const acknowledgeVoucher = useAcknowledgeVoucher();
  const checkVoucher = useCheckVoucher();
  const { comments } = useFetchVoucherComments(voucher.id, flag);

  // Column definitions
  const voucherCommentCols = [
    {
      title: "From",
      dataIndex: "sender",
      width: "20%",
    },
    {
      title: "To",
      dataIndex: "receiver",
      render: (receiver) => receiver || "PV Initiator",
      width: "20%",
    },
    {
      title: "Comment",
      dataIndex: "content",
      width: "40%",
    },
    {
      title: "Date",
      dataIndex: "dateString",
      width: "15%",
    },
    {
      title: "Actions",
      render: (_, item) => <CommentEdit comment={item} voucher={voucher} />,
      width: "5%",
    },
  ];

  const voucherItemsColumns = [
    {
      title: "Item",
      dataIndex: "particular",
      render: (item) => item.title,
      width: "50%",
    },
    {
      title: "Code",
      render: (_, item) => item?.particular?.value || "",
      width: "20%",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount) => `${NAIRA} ${formatCurrency(amount)}`,
      width: "30%",
    },
  ];

  const inflowOutflowColumns = [
    {
      title: "Date",
      dataIndex: "date",
      render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : 'N/A',
      width: "20%",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount) => `${NAIRA} ${formatCurrency(amount)}`,
      width: "20%",
    },
    {
      title: "Source",
      render: (_, record) => record.sourceAccount?.title || 'N/A',
      width: "30%",
    },
    {
      title: "Destination",
      render: (_, record) => record.destinationAccount?.title || 'N/A',
      width: "30%",
    },
  ];

  // Memoized calculations
  const totalTaxPercentage = useMemo(() => {
    return voucher?.taxes?.reduce((sum, tax) => sum + tax.charge, 0) || 0;
  }, [voucher?.taxes]);

  const voucherStatus = useMemo(() => {
    if (voucher.closed) return { text: "Closed", color: "default" };
    if (voucher.settled) return { text: "Settled", color: "success" };
    if (voucher.rejected) return { text: "Rejected", color: "error" };
    if (voucher.finalApproved) return { text: "Approved", color: "success" };
    if (voucher.submitted) return { text: "Pending", color: "processing" };
    return { text: "Draft", color: "warning" };
  }, [voucher]);

  // Handlers
  const handleModalAction = async (action, successMessage) => {
    try {
      await action();
      message.success(successMessage);
      setIsOpen(false);
      setFlag(!flag);
    } catch (error) {
      message.error("Operation failed");
    }
  };

  const handleRequestApproval = () =>
    handleModalAction(
      () => requestApproval(voucher.id),
      "Voucher submitted for approval"
    );

  const handleApprove = () =>
    handleModalAction(
      () => approve(voucher.id),
      "Voucher approved successfully"
    );

  const handleDisapprove = () =>
    handleModalAction(
      () => disapprove(voucher.id),
      "Voucher rejected successfully"
    );

  const handleDelete = () =>
    handleModalAction(
      () => deleteVoucher(voucher.id),
      "Voucher deleted successfully"
    );

  const handleCheck = () =>
    handleModalAction(
      () => checkVoucher(voucher.id),
      "Voucher checked successfully"
    );

  const handleAcknowledge = () =>
    handleModalAction(
      () => acknowledgeVoucher(voucher.id),
      "Payment acknowledged successfully"
    );

  const handleChequeToggle = async () => {
    const resp = await toggleCheque(voucher.id);
    message.success(`Cheque is ${resp.chequeReady ? "ready" : "not ready"}`);
    setIsOpen(false);
    setFlag(!flag);
  };

  const handleToggleClose = () =>
    handleModalAction(
      () => toggleClose(voucher.id),
      "Voucher closed successfully"
    );

  const handleSettlePV = async () => {
    if (!chequeNumber) {
      message.error("Please enter a cheque number");
      return;
    }
    await settlePV(voucher.id, chequeNumber);
    message.success("Voucher settled successfully");
    setIsSettleModalOpen(false);
    setFlag(!flag);
  };

  const handleVoucherRetirementUpload = async ({ file }) => {
    const formData = new FormData();
    formData.append("pv", voucher.id);
    formData.append("file", file);
    await uploadRetirementDoc(formData);
    message.success("Retirement document uploaded");
    setIsOpen(false);
  };

  const handleCheckerSearch = async (value) => {
    if (!value) {
      setCheckerOptions([]);
      return;
    }
    setCheckerSearchLoading(true);
    try {
      const users = await searchUsers(value);
      setCheckerOptions(users.map(user => ({
        value: user.id,
        label: user.name
      })));
    } catch (error) {
      message.error("Failed to search users");
    } finally {
      setCheckerSearchLoading(false);
    }
  };

  const handleSetChecker = async () => {
    if (!selectedChecker) {
      message.error("Please select a checker");
      return;
    }
    await setChecker(voucher.id, selectedChecker);
    message.success("Checker set successfully");
    setFlag(!flag);
  };

  const dispatchEditVoucherEvent = () => {
    setIsOpen(false);
    const event = new CustomEvent("voucher:edit", {
      detail: { voucher }
    });
    document.dispatchEvent(event);
  };

  const addComment = async () => {
    const content = commentAreaRef.current?.resizableTextArea?.textArea?.value;
    if (!content) {
      message.error("Please enter a comment");
      return;
    }
    await comment({
      pv: voucher.id,
      content,
      to: selectedRoleForComment,
    });
    message.success("Comment added");
    setIsCommentOpen(false);
    setFlag(!flag);
  };

  return (
    <>
      <Button type="primary" icon={<EyeOutlined />} onClick={() => setIsOpen(true)}>
        View
      </Button>

      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <FileTextOutlined style={{ fontSize: "20px" }} />
            <span>Voucher Details</span>
            <Badge status={voucherStatus.color} text={voucherStatus.text} />
          </div>
        }
        open={isOpen}
        onCancel={() => setIsOpen(false)}
        footer={null}
        width={900}
        bodyStyle={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        {/* Header Information */}
        <Card size="small" style={{ marginBottom: "1rem" }}>
          <Descriptions column={2} size="small">
            <Descriptions.Item label="Voucher No">{voucher.pvNumber}</Descriptions.Item>
            <Descriptions.Item label="Title">{voucher.name}</Descriptions.Item>
            <Descriptions.Item label="Prepared By">{voucher.creator?.name}</Descriptions.Item>
            <Descriptions.Item label="Date">{dayjs(voucher.createdAt).format('YYYY-MM-DD')}</Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>{voucher.description}</Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Financial Summary */}
        <Card size="small" style={{ marginBottom: "1rem" }}>
          <Space size="large" style={{ width: "100%", justifyContent: "space-around" }}>
            <Statistic
              title="Total Amount"
              value={voucher.total}
              prefix={NAIRA}
              formatter={(value) => formatCurrency(value)}
            />
            <Statistic
              title="Tax Amount"
              value={voucher.taxTotal || 0}
              prefix={NAIRA}
              formatter={(value) => formatCurrency(value)}
              suffix={`(${totalTaxPercentage}%)`}
            />
            <Statistic
              title="Net Amount"
              value={voucher.total - (voucher.taxTotal || 0)}
              prefix={NAIRA}
              formatter={(value) => formatCurrency(value)}
            />
          </Space>
        </Card>

        {/* Particulars */}
        {voucher?.particulars?.length > 0 && (
          <Card title="Particulars" size="small" style={{ marginBottom: "1rem" }}>
            <DataTable
              cols={voucherItemsColumns}
              data={voucher.particulars}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        )}

        {/* Inflows and Outflows */}
        {voucher?.inflowsAndOutflows?.length > 0 && (
          <Card title="Inflows & Outflows" size="small" style={{ marginBottom: "1rem" }}>
            <DataTable
              cols={inflowOutflowColumns}
              data={voucher.inflowsAndOutflows}
              pagination={false}
              size="small"
              rowKey={(_, idx) => `flow-${idx}`}
            />
          </Card>
        )}

        {/* Taxes */}
        {voucher?.taxes?.length > 0 && (
          <Card title="Taxes" size="small" style={{ marginBottom: "1rem" }}>
            <List
              size="small"
              dataSource={voucher.taxes}
              renderItem={(tax, idx) => (
                <List.Item
                  extra={<Text strong>{tax.charge}%</Text>}
                >
                  <Text>{idx + 1}. {tax.title}</Text>
                </List.Item>
              )}
            />
          </Card>
        )}

        {/* Approvals */}
        {voucher.approvedBy?.length > 0 && (
          <Card title="Approvals" size="small" style={{ marginBottom: "1rem" }}>
            <List
              size="small"
              dataSource={voucher.approvedBy}
              renderItem={(role, idx) => (
                <List.Item>
                  {idx + 1}. {role.replace(/_/g, " ")}
                </List.Item>
              )}
            />
          </Card>
        )}

        {/* Rejection Alert */}
        {voucher.rejectedAt && (
          <Alert
            message={`Rejected by: ${voucher.rejectedAt.role.replace("_", " ")}`}
            type="error"
            style={{ marginBottom: "1rem" }}
          />
        )}

        {/* Comments */}
        <Card title="Comments" size="small" style={{ marginBottom: "1rem" }}>
          <DataTable
            cols={voucherCommentCols}
            data={comments}
            pagination={false}
            size="small"
            locale={{ emptyText: <Empty description="No comments" /> }}
          />
          {voucher?.submitted && !voucher.finalApproved && (
            <Button
              type="link"
              onClick={() => setIsCommentOpen(true)}
              style={{ marginTop: "0.5rem" }}
            >
              Add Comment
            </Button>
          )}
        </Card>

        {/* Actions */}
        <Card title="Actions" size="small">
          <Space direction="vertical" style={{ width: "100%" }} size="middle">
            {/* Document Actions */}
            <div>
              <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "8px" }}>
                DOCUMENTS
              </Text>
              <Space wrap>
                <DocumentActions
                  onPreviewMemo={() => downloadMemo(voucher.memo.id)}
                  onPreviewVoucher={() => downloadVoucher(voucher.id)}
                />
                <RetirementActions
                  voucher={voucher}
                  currentUser={currentUser}
                  onUpload={handleVoucherRetirementUpload}
                  onDownload={() => downloadRet(voucher.id)}
                />
              </Space>
            </div>

            {/* Voucher Actions */}
            <div>
              <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "8px" }}>
                VOUCHER ACTIONS
              </Text>
              <Space wrap>
                <CreatorActions
                  voucher={voucher}
                  currentUser={currentUser}
                  onEdit={dispatchEditVoucherEvent}
                  onSubmit={handleRequestApproval}
                  onDelete={handleDelete}
                />

                <ApprovalActions
                  voucher={voucher}
                  currentUser={currentUser}
                  onApprove={handleApprove}
                  onReject={handleDisapprove}
                />

                <CashOfficeActions
                  voucher={voucher}
                  currentUser={currentUser}
                  onChequeToggle={handleChequeToggle}
                  onClose={handleToggleClose}
                />

                <BursarActions
                  voucher={voucher}
                  currentUser={currentUser}
                  onSettle={() => setIsSettleModalOpen(true)}
                />

                <CheckerActions
                  voucher={voucher}
                  currentUser={currentUser}
                  onCheck={handleCheck}
                />

                <AcknowledgmentActions
                  voucher={voucher}
                  currentUser={currentUser}
                  onAcknowledge={handleAcknowledge}
                />
              </Space>
            </div>

            {/* Checker Management */}
            {(currentUser?.id === voucher.creator.id && !voucher.submitted) && (
              <div>
                <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "8px" }}>
                  CHECKER MANAGEMENT
                </Text>
                <CheckerSelection
                  voucher={voucher}
                  currentUser={currentUser}
                  checkerOptions={checkerOptions}
                  checkerSearchLoading={checkerSearchLoading}
                  onSearch={handleCheckerSearch}
                  onSelect={setSelectedChecker}
                  onSetChecker={handleSetChecker}
                  selectedChecker={selectedChecker}
                />
              </div>
            )}

            {/* Information Display */}
            {(voucher.checker || voucher.chequeNumber) && (
              <div>
                <Text type="secondary" style={{ fontSize: "12px", display: "block", marginBottom: "8px" }}>
                  INFORMATION
                </Text>
                <Space direction="vertical">
                  <CheckerInfo checker={voucher.checker} />
                  <ChequeInfo chequeNumber={voucher.chequeNumber} />
                </Space>
              </div>
            )}
          </Space>
        </Card>
      </Modal>

      {/* Comment Modal */}
      <Modal
        title="Add Comment"
        open={isCommentOpen}
        onCancel={() => setIsCommentOpen(false)}
        onOk={addComment}
        okText="Submit"
      >
        <Form layout="vertical">
          <Form.Item label="Send to">
            <Select
              placeholder="Select recipient"
              onChange={setSelectedRoleForComment}
              style={{ width: "100%" }}
            >
              {voucher.approvedBy?.map((role, idx) => (
                <Option key={idx} value={role}>
                  {role.replace(/_/g, " ")}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Comment">
            <TextArea
              ref={commentAreaRef}
              placeholder="Enter your comment"
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Settle Modal */}
      <Modal
        title="Settle Voucher"
        open={isSettleModalOpen}
        onCancel={() => setIsSettleModalOpen(false)}
        onOk={handleSettlePV}
        okText="Settle"
        okButtonProps={{ disabled: !chequeNumber }}
      >
        <Form layout="vertical">
          <Form.Item
            label="Cheque Number"
            required
            rules={[{ required: true, message: "Please enter cheque number" }]}
          >
            <Input
              placeholder="Enter cheque number"
              value={chequeNumber}
              onChange={(e) => setChequeNumber(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default VoucherEditRefactored;
